/** D20/D21 · 行動版首頁（功能磚）。問候 + 今天行程 + 語音快捷 + 依權限過濾的行動版功能磚。 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardActionArea, Chip, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';

import { useAuth } from '../../../contexts/AuthContext';
import { usePermission } from '../../../hooks/usePermission';
import { useChatModeAvailable } from '../../../hooks/useChatModeAvailable';
import { systemService } from '../../../api/systemService';
import { flattenFunctions } from '../../../utils/functionsTree';
import { FuncType } from '../../../utils/funcType';
import { getI18nValue } from '../../../utils/i18nHelper';
import { formatLocalDate, formatLocalTimeShort } from '../../../utils/dateHelper';
import { getEvents } from '../../../services/eeMyCalendarService';
import { EeMyCalendar } from '../../../types/eeMyCalendar';
import type { SystemFunction } from '../../../types';

const CAL = 'ee_my_calendar';

const PWAHomePage: React.FC = () => {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const chatAvailable = useChatModeAvailable();

  const [functions, setFunctions] = React.useState<SystemFunction[]>([]);
  const [events, setEvents] = React.useState<EeMyCalendar[]>([]);

  const canReadCal = hasPermission(CAL, 'read');
  const canCreateCal = hasPermission(CAL, 'create');

  React.useEffect(() => {
    systemService.getFunctions()
      .then((tree) => setFunctions(flattenFunctions(tree)))
      .catch(() => { /* silent ok: 功能磚載入失敗不阻擋首頁 */ });
  }, []);

  React.useEffect(() => {
    if (!canReadCal) return;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    getEvents({ start: start.toISOString(), end: end.toISOString(), expand: true })
      .then((list) => setEvents([...list]
        .sort((a, b) => (a.dtstart || '').localeCompare(b.dtstart || '')).slice(0, 5)))
      .catch(() => { /* silent ok: 今天行程載入失敗 */ });
  }, [canReadCal]);

  // 功能磚：有 mobile_path 的 PAGE 葉，依頂層祖先分組。
  // 只排帳號工具葉（my_profile/change_password/logout/home·本就無 mobile_path）；
  // 個人化功能下有 mobile_path 的功能（如「我的履歷」）照常出磚，分組於「個人化功能」。
  const ACCOUNT_UTIL = new Set(['my_profile', 'change_password', 'logout', 'home']);
  const byId = React.useMemo(() => {
    const m = new Map<number, SystemFunction>();
    functions.forEach((f) => m.set(f.id, f));
    return m;
  }, [functions]);
  const topAncestor = React.useCallback((f: SystemFunction): SystemFunction => {
    let cur = f; const seen = new Set<number>();
    while (cur.upper_func_id && byId.has(cur.upper_func_id) && !seen.has(cur.id)) {
      seen.add(cur.id); cur = byId.get(cur.upper_func_id)!;
    }
    return cur;
  }, [byId]);
  const groups = React.useMemo(() => {
    const leaves = functions.filter((f) => f.func_type === FuncType.PAGE && !!f.mobile_path);
    const g = new Map<string, { title: string; items: SystemFunction[] }>();
    leaves.forEach((f) => {
      if (ACCOUNT_UTIL.has(f.func_code)) return;
      const top = topAncestor(f);
      const key = String(top.id);
      if (!g.has(key)) g.set(key, { title: getI18nValue(top.func_name, i18n.language), items: [] });
      g.get(key)!.items.push(f);
    });
    return Array.from(g.values()).filter((x) => x.items.length > 0);
  }, [functions, i18n.language, topAncestor]);

  const hour = new Date().getHours();
  const greetKey = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const greetDefault = hour < 12 ? '早安' : hour < 18 ? '午安' : '晚安';
  const todayStr = `${formatLocalDate(new Date().toISOString())}（${
    [t('pwaHome.sun', '日'), t('pwaHome.mon', '一'), t('pwaHome.tue', '二'), t('pwaHome.wed', '三'),
      t('pwaHome.thu', '四'), t('pwaHome.fri', '五'), t('pwaHome.sat', '六')][new Date().getDay()]}）`;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* 問候 */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t(`pwaHome.${greetKey}`, greetDefault)}，{user?.username || ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">{todayStr}</Typography>
      </Box>

      {/* 今天的行程 */}
      {canReadCal && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('pwaHome.today', '今天的行程')}
          </Typography>
          {events.length === 0 ? (
            <Typography variant="body2" color="text.disabled">{t('pwaHome.noEvent', '今天沒有行程')}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {events.map((e) => (
                <Card key={`${e.id}-${e.dtstart}`} variant="outlined">
                  <CardActionArea onClick={() => nav('/ee_pwa_calendar')} sx={{ p: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {e.is_all_day ? t('pwaHome.allDay', '整天') : formatLocalTimeShort(e.dtstart)} · {e.summary}
                    </Typography>
                    {e.location && (
                      <Typography variant="caption" color="text.secondary">📍 {e.location}</Typography>
                    )}
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* 語音快捷 */}
      {canCreateCal && (
        <Card variant="outlined" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <CardActionArea onClick={() => nav('/ee_pwa_calendar/voice-create')}
            sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <MicIcon />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('pwaHome.voiceShortcut', '說話新增行程或會議')}
            </Typography>
          </CardActionArea>
        </Card>
      )}

      {/* 功能磚 */}
      {groups.length > 0 ? (
        groups.map((grp, gi) => (
          <Box key={gi}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{grp.title}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              {grp.items.map((f) => (
                <Card key={f.func_code} variant="outlined">
                  <CardActionArea onClick={() => f.mobile_path && nav(f.mobile_path)}
                    sx={{ p: 1.5, minHeight: 84, display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', gap: 0.5, textAlign: 'center' }}>
                    <Box sx={{ fontSize: 26, lineHeight: 1 }}>{f.func_icon || '▧'}</Box>
                    <Typography variant="caption" sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}>
                      {getI18nValue(f.func_name, i18n.language)}
                    </Typography>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Box>
        ))
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Chip label={t('pwaHome.noMobileFunc',
            chatAvailable ? '目前沒有可在手機使用的功能，可切換到聊天模式使用' : '目前沒有可在手機使用的功能')}
            sx={{ height: 'auto', py: 1, whiteSpace: 'normal' }} />
        </Box>
      )}
    </Box>
  );
};

export default PWAHomePage;
